import { javaCompare, audienceDisplayName } from '../exportData.ts';
import { parseJavaProperties } from '../javaProperties.ts';
import { javaIsBlank, javaStrip } from '../javaText.ts';
import { ENTITY_TYPE_NAMES } from '../model.ts';
import { parseAudience } from '../queries.ts';
import { int, text, type SqlConnection, type SqlRow } from '../sql.ts';
import { stripTags } from '../tables/tableCodec.ts';
import { displayDate, type ChronologyDate } from '../calendar/chronologyDate.ts';
import { SEVERITY_GLYPHS, SEVERITY_LABELS, checkRealm, fingerprint, type Finding, type Severity } from '../consistency/consistency.ts';
import { dateOf, type Fields } from '../consistency/worldFacts.ts';
import { getCampaign, listCampaigns, listEncounters, listQuests, listSessions, type CampaignSession, type EncounterSummary, type Quest } from '../campaign/campaigns.ts';
import { getEncounter, type Combatant } from '../campaign/run.ts';
import { listStoryProjects, storyOutline } from '../story/story.ts';
import { getScene } from '../story/story.ts';

/**
 * Knowledge Modes (docs/PARITY-STEP-5-PLAN.md section 9), ported from the desktop's `modes`
 * package: five ways of moving through the same world, each a chain of columns where
 * picking something in one column fills the next with what's connected to it. The world is
 * one read-only snapshot (`ModeWorld`); the columns (`ModeNavigator`) and the preview
 * (`ModeDetails`) are pure functions of it, so the page works them out as you click.
 */

export type KnowledgeMode = 'EXPLORER' | 'WRITER' | 'GAME_MASTER' | 'ARCHITECT' | 'RESEARCH';

export interface ModeInfo {
  readonly label: string;
  readonly glyph: string;
  readonly tagline: string;
  /** Column titles, left to right. */
  readonly columns: readonly string[];
}

export const KNOWLEDGE_MODES: Readonly<Record<KnowledgeMode, ModeInfo>> = {
  EXPLORER: {
    label: 'Explorer',
    glyph: '🗺',
    tagline: 'Browse the world the way a traveller would - from the map to a place, its lore, its people, and their history.',
    columns: ['Atlas', 'Location', 'Lore', 'People', 'History'],
  },
  WRITER: {
    label: 'Writer',
    glyph: '✍',
    tagline: 'Write with the world at hand - pick a scene and see its characters, where they are, and the lore around them.',
    columns: ['Story', 'Scene', 'Characters', 'Locations', 'Lore'],
  },
  GAME_MASTER: {
    label: 'Game Master',
    glyph: '🎲',
    tagline: "Run a game - from tonight's session to an encounter, its NPCs, where they are, and what the players mustn't see yet.",
    columns: ['Campaign', 'Session', 'Encounter', 'NPC', 'Location', 'Secret'],
  },
  ARCHITECT: {
    label: 'Architect',
    glyph: '🏛',
    tagline: "Design the world's structure - its most connected entries, their history, the factions involved, and the cultures behind them.",
    columns: ['Graph', 'Timeline', 'Factions', 'Cultures'],
  },
  RESEARCH: {
    label: 'Research',
    glyph: '🔬',
    tagline: "Find what's missing or inconsistent - contradictions, unused characters, unknown locations, undated people, missing relationships.",
    columns: ['Question', 'Results', 'Involved'],
  },
};
export const MODES: readonly KnowledgeMode[] = ['EXPLORER', 'WRITER', 'GAME_MASTER', 'ARCHITECT', 'RESEARCH'];

export type ItemKind = 'ENTRY' | 'MAP' | 'PIN' | 'REGION' | 'EVENT' | 'STORY' | 'SCENE' | 'CAMPAIGN' | 'SESSION' | 'ENCOUNTER' | 'COMBATANT' | 'QUESTION' | 'FINDING';

/** One row in a column. `entryId` is the lore entry behind it, which the next column follows. */
export interface ModeItem {
  readonly kind: ItemKind;
  readonly refId: string;
  readonly title: string;
  readonly subtitle: string;
  readonly glyph: string;
  readonly entryId: string | null;
  /** Players shouldn't see it: a hidden pin, a locked or GM-only entry. */
  readonly secret: boolean;
}

/** Identity within a column, for keeping a selection. */
export const itemKey = (item: ModeItem) => `${item.kind}:${item.refId}`;

export interface ModeColumn {
  readonly title: string;
  readonly items: readonly ModeItem[];
  readonly emptyText: string;
}

// ---- the world (ModeWorld) ----

export interface ModeEntry {
  readonly id: string;
  readonly entityType: string;
  readonly typeName: string;
  readonly title: string;
  /** The content as plain text; empty when locked. */
  readonly text: string;
  readonly locked: boolean;
  readonly fields: Fields;
  readonly labels: readonly string[];
  /** `AudienceLevel.displayName`. */
  readonly audience: string;
  readonly secret: boolean;
}

export interface ModeLink {
  readonly sourceEntryId: string;
  readonly targetEntryId: string;
  readonly relationshipType: string;
}

export interface ModeEvent {
  readonly id: string;
  readonly title: string;
  readonly description: string;
  readonly start: ChronologyDate;
  readonly end: ChronologyDate | null;
  readonly entryIds: readonly string[];
  readonly markerIds: readonly string[];
}

export interface ModePlaced {
  readonly id: string;
  readonly label: string;
  readonly notes: string;
  readonly visible: boolean;
  readonly linkedEntryId: string | null;
  readonly linkedMapId: string | null;
}

export interface ModeMap {
  readonly id: string;
  readonly name: string;
  readonly markers: readonly ModePlaced[];
  readonly regions: readonly ModePlaced[];
}

export interface ModeScene {
  readonly id: string;
  readonly title: string;
  readonly text: string;
  readonly storyBeats: string;
  readonly timelinePosition: ChronologyDate | null;
  readonly entryIds: readonly string[];
  readonly eventIds: readonly string[];
  readonly actTitle: string;
  readonly chapterTitle: string;
}

export interface ModeStory {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly scenes: readonly ModeScene[];
}

export interface ModeCampaign {
  readonly id: string;
  readonly name: string;
  readonly description: string;
  readonly playerCharacterIds: readonly string[];
  readonly castIds: readonly string[];
  readonly sessions: readonly CampaignSession[];
  readonly encounters: readonly (EncounterSummary & { readonly combatants: readonly Combatant[] })[];
  readonly quests: readonly Quest[];
}

export interface ModeWorldData {
  readonly entries: readonly ModeEntry[];
  readonly links: readonly ModeLink[];
  readonly events: readonly ModeEvent[];
  readonly maps: readonly ModeMap[];
  readonly stories: readonly ModeStory[];
  readonly campaigns: readonly ModeCampaign[];
  readonly findings: readonly Finding[];
}

interface Neighbor {
  readonly entryId: string;
  readonly relationshipType: string;
  readonly outgoing: boolean;
}

const lowerTitle = (s: string) => s.toLowerCase();

class ModeWorld {
  readonly entries: readonly ModeEntry[];
  readonly events: readonly ModeEvent[];
  readonly #byId = new Map<string, ModeEntry>();
  readonly #neighbors = new Map<string, Neighbor[]>();

  constructor(readonly data: ModeWorldData) {
    // By title without regard to case; stable, so ties keep the realm's order.
    this.entries = [...data.entries].sort((a, b) => javaCompare(lowerTitle(a.title), lowerTitle(b.title)));
    this.events = [...data.events].sort((a, b) => (a.start.sortKey < b.start.sortKey ? -1 : a.start.sortKey > b.start.sortKey ? 1 : 0));
    for (const e of this.entries) if (!this.#byId.has(e.id)) this.#byId.set(e.id, e);
    for (const link of data.links) {
      if (link.sourceEntryId === link.targetEntryId) continue;
      this.#push(link.sourceEntryId, { entryId: link.targetEntryId, relationshipType: link.relationshipType, outgoing: true });
      this.#push(link.targetEntryId, { entryId: link.sourceEntryId, relationshipType: link.relationshipType, outgoing: false });
    }
  }

  #push(id: string, n: Neighbor) {
    const list = this.#neighbors.get(id);
    if (list) list.push(n);
    else this.#neighbors.set(id, [n]);
  }

  entry(id: string | null): ModeEntry | undefined {
    return id === null ? undefined : this.#byId.get(id);
  }

  title(id: string | null): string {
    return this.entry(id)?.title ?? '(missing entry)';
  }

  /** Every entry linked to or from this one, existing entries only. */
  neighbors(entryId: string): Neighbor[] {
    return (this.#neighbors.get(entryId) ?? []).filter((n) => this.#byId.has(n.entryId));
  }

  /** Distinct neighbors of any of the entries (not those entries themselves) of the given types, by title. */
  neighborsOfType(entryIds: ReadonlySet<string>, types: ReadonlySet<string>): Neighbor[] {
    const found = new Map<string, Neighbor>();
    for (const id of entryIds) {
      for (const n of this.neighbors(id)) {
        if (!entryIds.has(n.entryId) && types.has(this.entry(n.entryId)!.entityType) && !found.has(n.entryId)) found.set(n.entryId, n);
      }
    }
    return [...found.values()].sort((a, b) => javaCompare(lowerTitle(this.title(a.entryId)), lowerTitle(this.title(b.entryId))));
  }

  /** Events linked to any of the entries, earliest first. */
  eventsFor(entryIds: ReadonlySet<string>): ModeEvent[] {
    return this.events.filter((e) => e.entryIds.some((id) => entryIds.has(id)));
  }

  /** Every entry some scene, campaign, event, or map pin or region makes use of. */
  usedEntryIds(): Set<string> {
    const used = new Set<string>();
    for (const s of this.data.stories) for (const scene of s.scenes) scene.entryIds.forEach((id) => used.add(id));
    for (const c of this.data.campaigns) {
      c.playerCharacterIds.forEach((id) => used.add(id));
      c.castIds.forEach((id) => used.add(id));
      for (const e of c.encounters) for (const cb of e.combatants) if (cb.linkedEntryId !== null) used.add(cb.linkedEntryId);
      for (const q of c.quests) q.entryIds.forEach((id) => used.add(id));
    }
    for (const e of this.events) e.entryIds.forEach((id) => used.add(id));
    for (const m of this.data.maps) for (const p of [...m.markers, ...m.regions]) if (p.linkedEntryId !== null) used.add(p.linkedEntryId);
    return used;
  }
}

/** Players shouldn't see it: locked, or for an audience narrower than Player (or a custom one). */
function isSecret(locked: boolean, role: string): boolean {
  return locked || role === 'CUSTOM' || role === 'OWNER' || role === 'EDITOR' || role === 'CONTRIBUTOR';
}

// ---- the columns (ModeNavigator) ----

const set = (...types: string[]) => new Set(types);
const ALL_TYPES = Object.keys(ENTITY_TYPE_NAMES);
const BEINGS = set('PERSON', 'CREATURE');
const PLACES = set('PLACE');
const FACTIONS = set('FACTION');
const CULTURES = set('CULTURE', 'RELIGION', 'SPECIES');
const LORE = new Set(ALL_TYPES.filter((t) => !BEINGS.has(t)));
const OTHER_LORE = new Set(ALL_TYPES.filter((t) => !BEINGS.has(t) && t !== 'PLACE'));
const ANY = new Set(ALL_TYPES);

const Q = {
  CONTRADICTIONS: 'contradictions',
  REVIEW: 'review',
  SUGGESTIONS: 'suggestions',
  UNUSED: 'unused-characters',
  UNKNOWN_LOCATIONS: 'unknown-locations',
  UNDATED: 'undated-people',
  MISSING_RELATIONSHIPS: 'missing-relationships',
  ALL_PLACES: 'all-places',
} as const;

const GLYPHS: Record<string, string> = {
  PERSON: '👤', PLACE: '📍', FACTION: '⚑', SPECIES: '🧬', CREATURE: '🐾', ITEM: '🗝', EVENT: '📜', CONCEPT: '💭', CULTURE: '🎭', TECHNOLOGY: '⚙', RELIGION: '✨', CUSTOM: '✦',
};
export const typeGlyph = (type: string) => GLYPHS[type] ?? '✦';

const count = (n: number, singular: string, plural = `${singular}s`) => `${n} ${n === 1 ? singular : plural}`;
const label = (t: string, fallback: string) => (javaIsBlank(t) ? fallback : t);
const eventDate = (e: ModeEvent) => displayDate(e.start);
const item = (kind: ItemKind, refId: string, title: string, subtitle: string, glyph: string, entryId: string | null = null, secret = false): ModeItem => ({ kind, refId, title, subtitle, glyph, entryId, secret });
const entryItem = (e: ModeEntry, subtitle: string) => item('ENTRY', e.id, e.title, subtitle, typeGlyph(e.entityType), e.id, e.secret);
const ids = (...values: (string | null)[]) => new Set(values.filter((v): v is string => v !== null));

/** Works out a mode's columns: what goes in column `index`, given what's picked in the ones before. */
export class ModeNavigator {
  readonly #w: ModeWorld;

  constructor(data: ModeWorldData) {
    this.#w = new ModeWorld(data);
  }

  column(mode: KnowledgeMode, index: number, selections: readonly ModeItem[]): ModeColumn {
    const title = KNOWLEDGE_MODES[mode].columns[index]!;
    const path = selections.slice(0, index);
    switch (mode) {
      case 'EXPLORER':
        return this.#explorer(title, index, path);
      case 'WRITER':
        return this.#writer(title, index, path);
      case 'GAME_MASTER':
        return this.#gameMaster(title, index, path);
      case 'ARCHITECT':
        return this.#architect(title, index, path);
      case 'RESEARCH':
        return this.#research(title, index, path);
    }
  }

  // ---- Explorer: Atlas -> Location -> Lore -> People -> History ----

  #explorer(title: string, index: number, path: readonly ModeItem[]): ModeColumn {
    const w = this.#w;
    if (index === 0) {
      const items = w.data.maps.map((m) => item('MAP', m.id, m.name, `${count(m.markers.length, 'pin')} · ${count(m.regions.length, 'region')}`, '🗺'));
      if (w.entries.some((e) => e.entityType === 'PLACE')) items.push(item('QUESTION', Q.ALL_PLACES, 'Every place', 'All place entries, on a map or not', '📍'));
      return { title, items, emptyText: 'No maps or places yet - import a map in the Atlas, or add Place entries.' };
    }
    if (index === 1) {
      const atlas = path[0]!;
      if (atlas.kind === 'QUESTION') return { title, items: this.#entriesOfType(PLACES), emptyText: 'No place entries yet.' };
      const map = w.data.maps.find((m) => m.id === atlas.refId);
      const items = map ? [...map.markers.map((p) => this.#pinItem(p)), ...map.regions.map((r) => this.#regionItem(r))] : [];
      items.sort((a, b) => javaCompare(a.title.toLowerCase(), b.title.toLowerCase()));
      return { title, items, emptyText: 'Nothing is placed on this map yet.' };
    }
    if (index === 2) {
      const location = path[1]!.entryId;
      if (location === null) return { title, items: [], emptyText: "This pin isn't linked to a lore entry, so there's nothing to follow from it. Link it in the Atlas." };
      return { title, items: [this.#self(location, 'The place itself'), ...this.#neighborItems(ids(location), LORE)], emptyText: '' };
    }
    if (index === 3) {
      const anchors = ids(path[1]!.entryId, path[2]!.entryId);
      const items = this.#neighborItems(anchors, BEINGS);
      const seen = new Set(items.map((i) => i.entryId));
      for (const event of w.eventsFor(anchors)) {
        for (const id of event.entryIds) {
          const e = w.entry(id);
          if (e && BEINGS.has(e.entityType) && !seen.has(e.id)) {
            seen.add(e.id);
            items.push(entryItem(e, `In "${event.title}"`));
          }
        }
      }
      return { title, items, emptyText: `No people are linked to ${this.#titles(anchors)} yet.` };
    }
    const anchor = path[3]!.entryId;
    return { title, items: this.#eventItems(w.eventsFor(ids(anchor))), emptyText: `${w.title(anchor)} isn't linked to any chronology events yet.` };
  }

  // ---- Writer: Story -> Scene -> Characters -> Locations -> Lore ----

  #writer(title: string, index: number, path: readonly ModeItem[]): ModeColumn {
    const w = this.#w;
    if (index === 0) {
      return { title, items: w.data.stories.map((s) => item('STORY', s.id, s.name, count(s.scenes.length, 'scene'), '📖')), emptyText: 'No stories yet - start one in Story Studio.' };
    }
    if (index === 1) {
      const story = w.data.stories.find((s) => s.id === path[0]!.refId);
      const items = (story?.scenes ?? []).map((s) => item('SCENE', s.id, s.title, `${s.actTitle} › ${s.chapterTitle} · ${count(s.entryIds.length, 'linked entry', 'linked entries')}`, '🎬'));
      return { title, items, emptyText: 'This story has no scenes yet.' };
    }
    const scene = this.#scene(path[1]!.refId);
    const sceneEntries = new Set(scene?.entryIds ?? []);
    if (index === 2) {
      const items = this.#linkedEntries(sceneEntries, BEINGS, 'In this scene');
      const seen = new Set(items.map((i) => i.entryId));
      for (const eventId of scene?.eventIds ?? []) {
        const event = w.events.find((e) => e.id === eventId);
        for (const id of event?.entryIds ?? []) {
          const e = w.entry(id);
          if (e && BEINGS.has(e.entityType) && !seen.has(e.id)) {
            seen.add(e.id);
            items.push(entryItem(e, `In "${event!.title}"`));
          }
        }
      }
      return { title, items, emptyText: "No characters are linked to this scene yet - link them in Story Studio's scene editor." };
    }
    if (index === 3) {
      const items = this.#linkedEntries(sceneEntries, PLACES, 'In this scene');
      const seen = new Set(items.map((i) => i.entryId));
      for (const i of this.#neighborItems(ids(path[2]!.entryId), PLACES)) {
        if (seen.has(i.entryId)) continue;
        seen.add(i.entryId);
        items.push(i);
      }
      return { title, items, emptyText: `Neither this scene nor ${w.title(path[2]!.entryId)} is linked to a place yet.` };
    }
    const items = this.#linkedEntries(sceneEntries, OTHER_LORE, 'In this scene');
    const seen = new Set(items.map((i) => i.entryId));
    for (const i of this.#neighborItems(ids(path[2]!.entryId, path[3]!.entryId), OTHER_LORE)) {
        if (seen.has(i.entryId)) continue;
        seen.add(i.entryId);
        items.push(i);
      }
    return { title, items, emptyText: 'No other lore is linked here yet.' };
  }

  // ---- Game Master: Campaign -> Session -> Encounter -> NPC -> Location -> Secret ----

  #gameMaster(title: string, index: number, path: readonly ModeItem[]): ModeColumn {
    const w = this.#w;
    if (index === 0) {
      return {
        title,
        items: w.data.campaigns.map((c) => item('CAMPAIGN', c.id, c.name, `${count(c.sessions.length, 'session')} · ${count(c.encounters.length, 'encounter')}`, '🎲')),
        emptyText: 'No campaigns yet - start one in the Game Master Toolkit.',
      };
    }
    const campaign = w.data.campaigns.find((c) => c.id === path[0]!.refId);
    if (index === 1) {
      const items: ModeItem[] = [];
      for (const s of campaign?.sessions ?? []) {
        const encounters = campaign!.encounters.filter((e) => e.sessionId === s.id).length;
        items.push(item('SESSION', s.id, `Session ${s.sessionNumber}${javaIsBlank(s.title) ? '' : `: ${s.title}`}`, count(encounters, 'encounter'), '📅'));
      }
      if (campaign?.encounters.some((e) => e.sessionId === null)) items.push(item('SESSION', '', 'Not yet scheduled', 'Encounters not assigned to a session', '⏳'));
      return { title, items, emptyText: 'This campaign has no sessions yet.' };
    }
    if (index === 2) {
      const sessionId = path[1]!.refId;
      const items = (campaign?.encounters ?? [])
        .filter((e) => (sessionId === '' ? e.sessionId === null : e.sessionId === sessionId))
        .map((e) => item('ENCOUNTER', e.id, e.name, `${e.difficulty[0]}${e.difficulty.slice(1).toLowerCase()} · ${count(e.combatants.length, 'combatant')}`, '⚔'));
      return { title, items, emptyText: 'No encounters in this session.' };
    }
    if (index === 3) {
      const encounter = campaign?.encounters.find((e) => e.id === path[2]!.refId);
      const items = (encounter?.combatants ?? [])
        .filter((c) => !c.playerCharacter)
        .map((c) => {
          const e = w.entry(c.linkedEntryId);
          return e
            ? item('ENTRY', e.id, c.name, `${e.typeName}${e.title === c.name ? '' : ` · ${e.title}`}`, typeGlyph(e.entityType), e.id, e.secret)
            : item('COMBATANT', c.id, c.name, 'Not linked to an entry', '⚔');
        });
      return { title, items, emptyText: 'This encounter has no non-player combatants.' };
    }
    if (index === 4) {
      const npc = path[3]!.entryId;
      if (npc === null) return { title, items: [], emptyText: "This combatant isn't linked to a lore entry, so its whereabouts can't be followed." };
      return { title, items: this.#neighborItems(ids(npc), PLACES), emptyText: `${w.title(npc)} isn't linked to any place yet.` };
    }
    const anchors = ids(path[3]!.entryId, path[4]!.entryId);
    return { title, items: this.#secrets(anchors), emptyText: `Nothing secret is recorded about ${this.#titles(anchors)}.` };
  }

  /** GM-only material about the entries: secret entries among them and their neighbours, and hidden pins and regions. */
  #secrets(anchors: ReadonlySet<string>): ModeItem[] {
    const w = this.#w;
    const items = new Map<string, ModeItem>();
    const candidates = new Set(anchors);
    for (const a of anchors) for (const n of w.neighbors(a)) candidates.add(n.entryId);
    for (const id of candidates) {
      const e = w.entry(id);
      if (e?.secret) items.set(`entry:${id}`, item('ENTRY', id, e.title, e.locked ? 'Locked entry' : `${e.audience}-only entry`, '🔒', id, true));
    }
    const drillDown = new Set<string>();
    for (const m of w.data.maps) for (const p of m.markers) if (p.linkedEntryId !== null && anchors.has(p.linkedEntryId) && p.linkedMapId !== null) drillDown.add(p.linkedMapId);
    for (const m of w.data.maps) {
      const under = drillDown.has(m.id);
      for (const p of m.markers) {
        if (!p.visible && (under || (p.linkedEntryId !== null && anchors.has(p.linkedEntryId)))) items.set(`pin:${p.id}`, item('PIN', p.id, label(p.label, 'Unlabeled pin'), `Hidden pin on ${m.name}`, '🙈', p.linkedEntryId, true));
      }
      for (const r of m.regions) {
        if (!r.visible && (under || (r.linkedEntryId !== null && anchors.has(r.linkedEntryId)))) items.set(`region:${r.id}`, item('REGION', r.id, label(r.label, 'Unnamed region'), `Hidden region on ${m.name}`, '🙈', r.linkedEntryId, true));
      }
    }
    return [...items.values()];
  }

  // ---- Architect: Graph -> Timeline -> Factions -> Cultures ----

  #architect(title: string, index: number, path: readonly ModeItem[]): ModeColumn {
    const w = this.#w;
    if (index === 0) {
      const degree = new Map<string, number>();
      for (const l of w.data.links) {
        if (w.entry(l.sourceEntryId) && w.entry(l.targetEntryId)) {
          degree.set(l.sourceEntryId, (degree.get(l.sourceEntryId) ?? 0) + 1);
          degree.set(l.targetEntryId, (degree.get(l.targetEntryId) ?? 0) + 1);
        }
      }
      const items = [...degree.entries()]
        .sort((a, b) => b[1] - a[1] || javaCompare(w.title(a[0]).toLowerCase(), w.title(b[0]).toLowerCase()))
        .slice(0, 60)
        .map(([id, n]) => entryItem(w.entry(id)!, count(n, 'connection')));
      return { title, items, emptyText: "No relationships recorded yet - link entries to see the world's structure." };
    }
    const hub = path[0]!.entryId!;
    if (index === 1) {
      const items = this.#eventItems(w.eventsFor(ids(hub)));
      if (items.length === 0) {
        const around = new Set(w.neighbors(hub).map((n) => n.entryId));
        for (const event of w.eventsFor(around)) {
          const via = event.entryIds.find((id) => around.has(id));
          items.push(item('EVENT', event.id, event.title, `${eventDate(event)} · via ${via === undefined ? '' : w.title(via)}`, '📅'));
        }
      }
      return { title, items, emptyText: `Neither ${w.title(hub)} nor anything linked to it appears in the chronology yet.` };
    }
    if (index === 2) {
      const factionIds = new Set<string>();
      const event = w.events.find((e) => e.id === path[1]!.refId);
      for (const id of event?.entryIds ?? []) if (w.entry(id) && FACTIONS.has(w.entry(id)!.entityType)) factionIds.add(id);
      if (w.entry(hub) && FACTIONS.has(w.entry(hub)!.entityType)) factionIds.add(hub);
      for (const i of this.#neighborItems(ids(hub), FACTIONS)) factionIds.add(i.entryId!);
      const items = [...factionIds].map((id) => entryItem(w.entry(id)!, this.#stance(id)));
      return { title, items, emptyText: `No factions are involved in this event or linked to ${w.title(hub)}.` };
    }
    const faction = path[2]!.entryId!;
    const items = this.#neighborItems(ids(faction), CULTURES);
    const seen = new Set(items.map((i) => i.entryId));
    const members = new Set(w.neighborsOfType(ids(faction), BEINGS).map((n) => n.entryId));
    for (const culture of w.neighborsOfType(members, CULTURES)) {
      if (!seen.has(culture.entryId)) {
        seen.add(culture.entryId);
        items.push(entryItem(w.entry(culture.entryId)!, 'Through its members'));
      }
    }
    return { title, items, emptyText: `No cultures, religions, or species are linked to ${w.title(faction)} or its members yet.` };
  }

  /** "2 members · 1 ally · 1 enemy" for a faction. */
  #stance(factionId: string): string {
    const n = this.#w.neighbors(factionId);
    const allies = n.filter((x) => x.relationshipType.includes('allied') || x.relationshipType.includes('ally')).length;
    const enemies = n.filter((x) => x.relationshipType.includes('enemy') || x.relationshipType.includes('war')).length;
    const members = n.filter((x) => !x.outgoing && x.relationshipType === 'member of').length;
    return `${count(members, 'member')} · ${count(allies, 'ally', 'allies')} · ${count(enemies, 'enemy', 'enemies')}`;
  }

  // ---- Research: Question -> Results -> Involved ----

  #research(title: string, index: number, path: readonly ModeItem[]): ModeColumn {
    const w = this.#w;
    if (index === 0) {
      const q = (id: string, name: string, glyph: string, n: number, subtitle: string) => item('QUESTION', id, `${name} (${n})`, subtitle, glyph);
      return {
        title,
        items: [
          q(Q.CONTRADICTIONS, 'Contradictions', '⛔', this.#findings('ERROR').length, "Facts that can't all be true"),
          q(Q.REVIEW, 'Needs review', '⚠', this.#findings('WARNING').length, 'Probably mistakes'),
          q(Q.SUGGESTIONS, 'Suggestions', '💡', this.#findings('INFO').length, 'Worth a look'),
          q(Q.UNUSED, 'Unused characters', '👤', this.unusedCharacters().length, 'In no scene, event, campaign, or map'),
          q(Q.UNKNOWN_LOCATIONS, 'Unknown locations', '❔', this.unknownLocations().length, 'People and events not tied to any place'),
          q(Q.UNDATED, 'Undated people', '⌛', this.undatedPeople().length, 'No "born" year recorded'),
          q(Q.MISSING_RELATIONSHIPS, 'Missing relationships', '🔗', this.missingRelationships().length, 'People and factions with no family, faction, or allies'),
        ],
        emptyText: '',
      };
    }
    if (index === 1) {
      const question = path[0]!.refId;
      const items =
        question === Q.CONTRADICTIONS ? this.#findingItems(this.#findings('ERROR'))
        : question === Q.REVIEW ? this.#findingItems(this.#findings('WARNING'))
        : question === Q.SUGGESTIONS ? this.#findingItems(this.#findings('INFO'))
        : question === Q.UNUSED ? this.unusedCharacters()
        : question === Q.UNKNOWN_LOCATIONS ? this.unknownLocations()
        : question === Q.UNDATED ? this.undatedPeople()
        : this.missingRelationships();
      return { title, items, emptyText: 'Nothing found - nice.' };
    }
    const result = path[1]!;
    if (result.kind === 'FINDING') {
      const finding = w.data.findings.find((f) => fingerprint(f) === result.refId);
      const items = finding ? [...new Set(finding.entryIds)].flatMap((id) => (w.entry(id) ? [entryItem(w.entry(id)!, w.entry(id)!.typeName)] : [])) : [];
      return { title, items, emptyText: "This finding isn't about particular entries." };
    }
    if (result.entryId === null) return { title, items: [], emptyText: '' };
    return { title, items: [this.#self(result.entryId, 'The entry itself'), ...this.#neighborItems(ids(result.entryId), ANY)], emptyText: '' };
  }

  #findings(severity: Severity): Finding[] {
    return this.#w.data.findings.filter((f) => f.severity === severity);
  }

  #findingItems(findings: readonly Finding[]): ModeItem[] {
    return findings.map((f) => item('FINDING', fingerprint(f), f.title, [...new Set(f.entryIds)].map((id) => this.#w.title(id)).join(', '), SEVERITY_GLYPHS[f.severity], f.entryIds[0] ?? null));
  }

  unusedCharacters(): ModeItem[] {
    const used = this.#w.usedEntryIds();
    return this.#w.entries.filter((e) => BEINGS.has(e.entityType) && !used.has(e.id)).map((e) => entryItem(e, `${e.typeName} · ${count(this.#w.neighbors(e.id).length, 'relationship')}`));
  }

  unknownLocations(): ModeItem[] {
    const w = this.#w;
    const items: ModeItem[] = [];
    for (const e of w.entries) if (e.entityType === 'PERSON' && w.neighborsOfType(ids(e.id), PLACES).length === 0) items.push(entryItem(e, 'Person · no place linked'));
    for (const event of w.events) {
      const placed = event.markerIds.length > 0 || event.entryIds.some((id) => w.entry(id)?.entityType === 'PLACE');
      if (!placed) items.push(item('EVENT', event.id, event.title, `${eventDate(event)} · no place or map pin`, '📅'));
    }
    return items;
  }

  undatedPeople(): ModeItem[] {
    return this.#w.entries.filter((e) => e.entityType === 'PERSON' && dateOf(e.fields, 'BIRTH') === null).map((e) => entryItem(e, 'Person · no "born" year'));
  }

  missingRelationships(): ModeItem[] {
    const w = this.#w;
    const items: ModeItem[] = [];
    for (const e of w.entries) {
      const n = w.neighbors(e.id).map((x) => w.entry(x.entryId)!);
      if (e.entityType === 'PERSON') {
        if (!n.some((x) => BEINGS.has(x.entityType)) && !n.some((x) => x.entityType === 'FACTION')) items.push(entryItem(e, 'Person · no family, allies, or faction'));
      } else if (e.entityType === 'FACTION') {
        if (!n.some((x) => x.entityType === 'FACTION' || BEINGS.has(x.entityType))) items.push(entryItem(e, 'Faction · no members, allies, or enemies'));
      }
    }
    return items;
  }

  // ---- shared ----

  #entriesOfType(types: ReadonlySet<string>): ModeItem[] {
    return this.#w.entries.filter((e) => types.has(e.entityType)).map((e) => entryItem(e, e.typeName));
  }

  #neighborItems(anchors: ReadonlySet<string>, types: ReadonlySet<string>): ModeItem[] {
    return this.#w.neighborsOfType(anchors, types).map((n) => {
      const e = this.#w.entry(n.entryId)!;
      return entryItem(e, `${e.typeName} · ${this.#relation(anchors, n)}`);
    });
  }

  /** "Asteria founded by Rowan Valen": the relationship written out, in its real direction. */
  #relation(anchors: ReadonlySet<string>, neighbor: Neighbor): string {
    const w = this.#w;
    for (const anchor of anchors) {
      for (const n of w.neighbors(anchor)) {
        if (n.entryId === neighbor.entryId) return n.outgoing ? `${w.title(anchor)} ${n.relationshipType} ${w.title(n.entryId)}` : `${w.title(n.entryId)} ${n.relationshipType} ${w.title(anchor)}`;
      }
    }
    return neighbor.relationshipType;
  }

  #linkedEntries(entryIds: ReadonlySet<string>, types: ReadonlySet<string>, subtitle: string): ModeItem[] {
    return [...entryIds]
      .flatMap((id) => this.#w.entry(id) ?? [])
      .filter((e) => types.has(e.entityType))
      .sort((a, b) => javaCompare(a.title.toLowerCase(), b.title.toLowerCase()))
      .map((e) => entryItem(e, `${e.typeName} · ${subtitle}`));
  }

  #self(entryId: string, subtitle: string): ModeItem {
    const e = this.#w.entry(entryId)!;
    return entryItem(e, `${e.typeName} · ${subtitle}`);
  }

  #pinItem(p: ModePlaced): ModeItem {
    const w = this.#w;
    const linked = w.entry(p.linkedEntryId);
    return item('PIN', p.id, label(p.label, 'Unlabeled pin'), p.linkedEntryId === null ? 'Pin · not linked' : `Pin · ${w.title(p.linkedEntryId)}${p.linkedMapId !== null ? ' · has its own map' : ''}`, '📍', linked?.id ?? null, !p.visible);
  }

  #regionItem(r: ModePlaced): ModeItem {
    const w = this.#w;
    return item('REGION', r.id, label(r.label, 'Unnamed region'), r.linkedEntryId === null ? 'Region · not linked' : `Region · ${w.title(r.linkedEntryId)}`, '⬠', w.entry(r.linkedEntryId)?.id ?? null, !r.visible);
  }

  #eventItems(events: readonly ModeEvent[]): ModeItem[] {
    return events.map((e) => item('EVENT', e.id, e.title, eventDate(e), '📅'));
  }

  #scene(id: string): ModeScene | undefined {
    for (const s of this.#w.data.stories) for (const scene of s.scenes) if (scene.id === id) return scene;
    return undefined;
  }

  #titles(anchors: ReadonlySet<string>): string {
    return [...anchors].map((id) => this.#w.title(id)).join(' or ');
  }

  // ---- the preview (ModeDetails) ----

  describe(selected: ModeItem): ModeDetail {
    return describe(this.#w, selected);
  }
}

/** The preview's summary of what's selected: a short dossier. */
export interface ModeDetail {
  readonly heading: string;
  /** What it is: "Person", "Hidden pin on Silverkeep", "Scene · Act I › Chapter 2". */
  readonly kind: string;
  readonly body: string;
  readonly facts: readonly string[];
  readonly related: readonly string[];
  readonly secret: boolean;
}

const EXCERPT = 600;
const MAX_CONNECTIONS = 14;

/** Up to 600 characters, whitespace run together (`ModeDetails.excerpt`). */
export function excerpt(textValue: string): string {
  const clean = javaStrip(textValue).replace(/[ \t\n\x0B\f\r]+/g, ' ');
  return clean.length <= EXCERPT ? clean : `${javaStrip(clean.slice(0, EXCERPT))}…`;
}

const cap = (lines: string[]) => (lines.length <= MAX_CONNECTIONS ? lines : [...lines.slice(0, MAX_CONNECTIONS), `… and ${lines.length - MAX_CONNECTIONS} more`]);
const unlabeled = (t: string) => (javaIsBlank(t) ? '(unlabeled)' : t);
const detail = (heading: string, kind: string, body = '', facts: string[] = [], related: string[] = [], secret = false): ModeDetail => ({ heading, kind, body, facts, related, secret });
const missing = (i: ModeItem) => detail(i.title, 'No longer exists');

function describe(w: ModeWorld, i: ModeItem): ModeDetail {
  switch (i.kind) {
    case 'ENTRY': {
      const e = w.entry(i.refId);
      if (!e) return missing(i);
      const facts = [...e.fields.map(([k, v]) => `${k}: ${v}`), ...(e.labels.length > 0 ? [`Labels: ${e.labels.join(', ')}`] : []), `Audience: ${e.audience}`];
      const related = w.neighbors(e.id).map((n) => (n.outgoing ? `${n.relationshipType} → ${w.title(n.entryId)}` : `${w.title(n.entryId)} → ${n.relationshipType} this`));
      for (const event of w.eventsFor(ids(e.id))) related.push(`📅 ${event.title} (${eventDate(event)})`);
      return detail(e.title, e.typeName + (e.secret ? ' · GM only' : ''), e.locked ? "🔒 This entry's content is locked." : excerpt(e.text), facts, cap(related), e.secret);
    }
    case 'MAP': {
      const m = w.data.maps.find((x) => x.id === i.refId);
      if (!m) return missing(i);
      const places = [...m.markers.map((p) => `📍 ${unlabeled(p.label)}${p.visible ? '' : ' (hidden)'}`), ...m.regions.map((r) => `⬠ ${unlabeled(r.label)}${r.visible ? '' : ' (hidden)'}`)];
      return detail(m.name, 'Map', '', [i.subtitle], cap(places));
    }
    case 'PIN':
    case 'REGION': {
      for (const m of w.data.maps) {
        const p = (i.kind === 'PIN' ? m.markers : m.regions).find((x) => x.id === i.refId);
        if (!p) continue;
        const facts: string[] = [];
        if (p.linkedEntryId !== null) facts.push(`Entry: ${w.title(p.linkedEntryId)}`);
        const opens = p.linkedMapId === null ? undefined : w.data.maps.find((x) => x.id === p.linkedMapId);
        if (opens) facts.push(`Opens map: ${opens.name}`);
        if (!p.visible) facts.push('Hidden from players');
        return detail(unlabeled(p.label), `${i.kind === 'PIN' ? 'Pin' : 'Region'} on ${m.name}`, excerpt(p.notes), facts, [], !p.visible);
      }
      return missing(i);
    }
    case 'EVENT': {
      const e = w.events.find((x) => x.id === i.refId);
      if (!e) return missing(i);
      const when = eventDate(e) + (e.end === null ? '' : ` – ${displayDate(e.end)}`);
      return detail(e.title, `Chronology event · ${when}`, excerpt(e.description), [], e.entryIds.map((id) => w.title(id)).sort(javaCompare));
    }
    case 'STORY': {
      const s = w.data.stories.find((x) => x.id === i.refId);
      if (!s) return missing(i);
      return detail(s.name, `Story · ${i.subtitle}`, excerpt(s.description), [], cap(s.scenes.map((x) => `🎬 ${x.title}`)));
    }
    case 'SCENE': {
      let scene: ModeScene | undefined;
      for (const s of w.data.stories) scene ??= s.scenes.find((x) => x.id === i.refId);
      if (!scene) return missing(i);
      const facts: string[] = [];
      if (!javaIsBlank(scene.storyBeats)) facts.push(`Beats: ${javaStrip(scene.storyBeats)}`);
      if (scene.timelinePosition) facts.push(`When: ${displayDate(scene.timelinePosition)}`);
      return detail(scene.title, `Scene · ${scene.actTitle} › ${scene.chapterTitle}`, excerpt(scene.text), facts, scene.entryIds.map((id) => w.title(id)).sort(javaCompare));
    }
    case 'CAMPAIGN': {
      const c = w.data.campaigns.find((x) => x.id === i.refId);
      if (!c) return missing(i);
      const related = [...c.castIds.map((id) => `Cast: ${w.title(id)}`), ...c.quests.map((q) => `Quest: ${q.title} (${q.status.toLowerCase()})`)];
      return detail(c.name, `Campaign · ${i.subtitle}`, excerpt(c.description), [], cap(related));
    }
    case 'SESSION': {
      for (const c of w.data.campaigns) {
        const s = c.sessions.find((x) => x.id === i.refId);
        if (s) return detail(i.title, `Session of ${c.name}`, excerpt(s.summary), javaIsBlank(s.notes) ? [] : [`GM notes: ${excerpt(s.notes)}`]);
      }
      return detail(i.title, 'Session', i.subtitle);
    }
    case 'ENCOUNTER': {
      for (const c of w.data.campaigns) {
        const e = c.encounters.find((x) => x.id === i.refId);
        if (e) return detail(e.name, `Encounter · ${i.subtitle}`, excerpt(e.description), [], e.combatants.map((cb) => `${cb.playerCharacter ? '🛡 ' : '⚔ '}${cb.name} - initiative ${cb.initiative}, HP ${cb.currentHp}/${cb.maxHp}`));
      }
      return missing(i);
    }
    case 'COMBATANT':
      return detail(i.title, 'Combatant', 'Not linked to a lore entry.');
    case 'QUESTION':
      return detail(i.title, 'Research question', i.subtitle);
    case 'FINDING': {
      const f = w.data.findings.find((x) => fingerprint(x) === i.refId);
      if (!f) return missing(i);
      return detail(f.title, `${SEVERITY_GLYPHS[f.severity]} ${SEVERITY_LABELS[f.severity]}`, f.message, [], [...new Set(f.entryIds)].map((id) => w.title(id)));
    }
  }
}

// ---- the snapshot (KnowledgeModeService) ----

function datesOf(row: SqlRow): { start: ChronologyDate; end: ChronologyDate | null } {
  return {
    start: { sortKey: BigInt(text(row['start_key']) || '0'), label: text(row['start_label']), uncertain: int(row['start_uncertain']) !== 0 },
    end: int(row['has_end']) !== 0 ? { sortKey: BigInt(text(row['end_key']) || '0'), label: text(row['end_label']), uncertain: int(row['end_uncertain']) !== 0 } : null,
  };
}

const optional = (v: unknown) => (v === null || v === undefined || String(v).trim() === '' ? null : String(v));

function placedOf(row: SqlRow): ModePlaced {
  return { id: text(row['id']), label: text(row['label']), notes: text(row['notes']), visible: int(row['visible']) !== 0, linkedEntryId: optional(row['linked_entry_id']), linkedMapId: optional(row['linked_map_id']) };
}

/** Everything the modes walk through, read as the desktop reads it, with the Consistency Check's active findings. */
export function modeWorld(conn: SqlConnection): ModeWorldData {
  const entries = conn.all('SELECT id, entity_type, custom_type_name, title, content_html, metadata, audience, locked FROM lore_entries ORDER BY title COLLATE NOCASE').map((row): ModeEntry => {
    const id = text(row['id']);
    const type = text(row['entity_type']);
    const custom = text(row['custom_type_name']);
    const locked = int(row['locked']) !== 0;
    const audience = parseAudience(text(row['audience']));
    return {
      id,
      entityType: type,
      typeName: type === 'CUSTOM' && custom.trim() !== '' ? custom : (ENTITY_TYPE_NAMES[type] ?? type),
      title: text(row['title']),
      text: locked ? '' : stripTags(text(row['content_html'])),
      locked,
      fields: [...parseJavaProperties(text(row['metadata'])).entries()],
      labels: [...new Set(conn.all('SELECT label FROM entry_labels WHERE entry_id = ?', [id]).map((r) => text(r['label']).trim().toLowerCase()).filter((l) => l !== ''))].sort(javaCompare),
      audience: audienceDisplayName(audience),
      secret: isSecret(locked, audience.role),
    };
  });
  const links = conn.all('SELECT source_entry_id, target_entry_id, relationship_type FROM entry_links').map((r) => ({ sourceEntryId: text(r['source_entry_id']), targetEntryId: text(r['target_entry_id']), relationshipType: text(r['relationship_type']) }));
  const dates = 'CAST(start_sort_key AS TEXT) AS start_key, start_label, start_uncertain, has_end, CAST(end_sort_key AS TEXT) AS end_key, end_label, end_uncertain';
  const events = conn.all(`SELECT id, title, description, ${dates} FROM chronology_events ORDER BY start_sort_key`).map((row) => {
    const id = text(row['id']);
    return {
      id,
      title: text(row['title']),
      description: text(row['description']),
      ...datesOf(row),
      entryIds: conn.all('SELECT entry_id FROM chronology_event_entries WHERE event_id = ?', [id]).map((r) => text(r['entry_id'])),
      markerIds: conn.all('SELECT marker_id FROM chronology_event_markers WHERE event_id = ?', [id]).map((r) => text(r['marker_id'])),
    };
  });
  const maps = conn.all('SELECT id, name FROM atlas_maps ORDER BY name COLLATE NOCASE').map((row) => {
    const id = text(row['id']);
    return {
      id,
      name: text(row['name']),
      markers: conn.all('SELECT id, label, notes, visible, linked_entry_id, linked_map_id FROM map_markers WHERE map_id = ? ORDER BY created', [id]).map(placedOf),
      regions: conn.all('SELECT id, label, notes, visible, linked_entry_id, linked_map_id FROM map_regions WHERE map_id = ? ORDER BY created', [id]).map(placedOf),
    };
  });
  const stories = listStoryProjects(conn).flatMap((p): ModeStory[] => {
    const outline = storyOutline(conn, p.id);
    if (!outline) return [];
    const scenes: ModeScene[] = [];
    for (const a of outline.acts) {
      for (const c of a.chapters) {
        for (const s of c.scenes) {
          const scene = getScene(conn, s.id);
          if (!scene) continue;
          scenes.push({ id: s.id, title: s.title, text: stripTags(scene.contentHtml), storyBeats: scene.storyBeats, timelinePosition: scene.timelinePosition, entryIds: scene.entryIds, eventIds: scene.eventIds, actTitle: a.act.title, chapterTitle: c.chapter.title });
        }
      }
    }
    return [{ id: p.id, name: outline.project.name, description: outline.project.description, scenes }];
  });
  const campaigns = listCampaigns(conn).flatMap((summary): ModeCampaign[] => {
    const c = getCampaign(conn, summary.id);
    if (!c) return [];
    return [
      {
        id: c.id,
        name: c.name,
        description: c.description,
        playerCharacterIds: c.playerCharacterIds,
        castIds: c.castIds,
        sessions: listSessions(conn, c.id),
        encounters: listEncounters(conn, c.id).map((e) => ({ ...e, combatants: getEncounter(conn, e.id)?.combatants ?? [] })),
        quests: listQuests(conn, c.id),
      },
    ];
  });
  return { entries, links, events, maps, stories, campaigns, findings: checkRealm(conn).active };
}
